// Prompt 0469 — Tab Ficha FG del AdminRolGobernanzaPanel.
// Editor Tiptap por seccion de la plantilla FG (5 secciones: mision, funciones,
// competencias, relaciones, indicadores). Clona el flujo de
// rol-funcional-panel/tabs/FichaTab pero con keys propias de FG y conversion
// del bloque "funciones" (bulletList Tiptap) a string[] al guardar.

import type React from 'react';
import { useState, useEffect } from 'react';
import { EditorContent } from '@tiptap/react';
import {
  fetchFichaRolGobernanzaContenido,
  actualizarFichaRolGobernanzaContenido,
  crearFichaRolGobernanza,
  type FichaFGSecciones,
  type FichaRolGobernanzaContenido,
} from '../../../../../../shared/services/api';
import { useI18n } from '../../../../../../../i18n';
import { LoadingSpinner } from '../../../../../../shared/components/LoadingSpinner';
import { FichaToolbar } from '../../ficha-shared/FichaToolbar';
import { useFichaEditor } from '../../ficha-shared/useFichaEditor';

type ProseMirrorNode = Record<string, unknown>;

type SeccionIdFG = 'mision' | 'funciones' | 'competencias' | 'relaciones' | 'indicadores';

const SECCIONES: Array<{ id: SeccionIdFG; labelKey: string; fallbackLabel: string }> = [
  { id: 'mision', labelKey: 'fichaMision', fallbackLabel: 'Misión del rol' },
  { id: 'funciones', labelKey: 'fichaFunciones', fallbackLabel: 'Funciones y responsabilidades' },
  {
    id: 'competencias',
    labelKey: 'fichaCompetencias',
    fallbackLabel: 'Competencias requeridas',
  },
  { id: 'relaciones', labelKey: 'fichaRelaciones', fallbackLabel: 'Relaciones clave' },
  { id: 'indicadores', labelKey: 'fichaIndicadores', fallbackLabel: 'Indicadores de desempeño' },
];

// Heading literal (como aparece en la plantilla backend) → SeccionIdFG. Tolerancia tilde/no-tilde
// para fichas antiguas. Backend usa titulos con tilde.
const HEADING_TO_SECCION: Record<string, SeccionIdFG> = {
  'Misión del Rol': 'mision',
  'Mision del Rol': 'mision',
  'Funciones y Responsabilidades': 'funciones',
  'Competencias Requeridas': 'competencias',
  'Relaciones Clave': 'relaciones',
  'Indicadores de Desempeño': 'indicadores',
  'Indicadores de Desempeno': 'indicadores',
};

const PENDING_TEXTS = new Set(['(Pendiente de redacción)', '(Pendiente de redaccion)']);

function textOfNode(node: ProseMirrorNode): string {
  const content = (node.content ?? []) as ProseMirrorNode[];
  return content
    .map((c) => (typeof c.text === 'string' ? c.text : ''))
    .join('')
    .trim();
}

function extractSeccionesFG(contenidoJson: unknown): Record<SeccionIdFG, ProseMirrorNode[]> {
  const result: Partial<Record<SeccionIdFG, ProseMirrorNode[]>> = {};
  const doc = contenidoJson as { content?: ProseMirrorNode[] } | null;
  const nodes = (doc?.content ?? []) as ProseMirrorNode[];
  let currentSection: SeccionIdFG | null = null;
  let buffer: ProseMirrorNode[] = [];

  const flush = () => {
    if (currentSection && buffer.length > 0) {
      const allPending = buffer.every(
        (n) => n.type === 'paragraph' && PENDING_TEXTS.has(textOfNode(n)),
      );
      result[currentSection] = allPending ? [] : buffer;
    }
  };

  for (const node of nodes) {
    if (node.type === 'heading') {
      flush();
      const title = textOfNode(node);
      currentSection = HEADING_TO_SECCION[title] ?? null;
      buffer = [];
      continue;
    }
    if (currentSection) buffer.push(node);
  }
  flush();
  return result as Record<SeccionIdFG, ProseMirrorNode[]>;
}

// Convierte el bloque Tiptap de "funciones" (normalmente bulletList) a string[]
// que el backend FG espera. Fallback: si es paragraph, partir por saltos.
function nodosToFuncionesArray(nodos: ProseMirrorNode[]): string[] {
  const bullet = nodos.find((n) => n.type === 'bulletList' || n.type === 'orderedList');
  if (bullet) {
    const items = (bullet.content ?? []) as ProseMirrorNode[];
    return items
      .map((li) => {
        const children = (li.content ?? []) as ProseMirrorNode[];
        return children
          .map((c) => textOfNode(c))
          .filter(Boolean)
          .join(' ');
      })
      .filter((s) => s.length > 0);
  }
  // Fallback: unir paragraphs por texto y partir por salto.
  const text = nodos
    .map((n) => textOfNode(n))
    .filter(Boolean)
    .join('\n');
  return text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
}

// Secciones de texto plano (las demas): concatena paragraphs.
function nodosToStringPlano(nodos: ProseMirrorNode[]): string {
  return nodos
    .map((n) => textOfNode(n))
    .filter(Boolean)
    .join('\n');
}

interface FichaTabProps {
  tipoRolId: string;
  isDirty: boolean;
  onMarkDirty: () => void;
  onMarkClean: () => void;
}

export function FichaTab({ tipoRolId, isDirty, onMarkDirty, onMarkClean }: FichaTabProps) {
  const { t } = useI18n();
  const adm = (t.structure as Record<string, unknown>)?.admin as
    | Record<string, unknown>
    | undefined;
  const lbl = (adm ?? {}) as Record<string, string>;
  const panel = (lbl.rolGobernanzaPanel ?? {}) as unknown as Record<string, string>;

  const [secciones, setSecciones] = useState<Record<SeccionIdFG, ProseMirrorNode[]>>(
    {} as Record<SeccionIdFG, ProseMirrorNode[]>,
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [fichaExists, setFichaExists] = useState<boolean>(false);
  const [toast, setToast] = useState('');
  const [error, setError] = useState('');
  const [focusedSeccion, setFocusedSeccion] = useState<SeccionIdFG | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  // Carga la ficha. Si backend devuelve 404 -> fichaExists=false (UI invita a crear).
  useEffect(() => {
    setLoading(true);
    setError('');
    fetchFichaRolGobernanzaContenido(tipoRolId)
      .then((res: { data: FichaRolGobernanzaContenido }) => {
        setSecciones(extractSeccionesFG(res.data.contenidoJson));
        setFichaExists(true);
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : '';
        if (/404|not\s*found/i.test(msg)) {
          setFichaExists(false);
        } else {
          setError(msg || (panel.errorCargarFicha ?? 'Error al cargar la ficha'));
        }
      })
      .finally(() => setLoading(false));
  }, [tipoRolId, panel.errorCargarFicha]);

  async function handleCreate() {
    setCreating(true);
    try {
      await crearFichaRolGobernanza(tipoRolId);
      const res = await fetchFichaRolGobernanzaContenido(tipoRolId);
      setSecciones(extractSeccionesFG(res.data.contenidoJson));
      setFichaExists(true);
      showToast(panel.fichaCreada ?? 'Ficha creada');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
    } finally {
      setCreating(false);
    }
  }

  async function handleGuardar() {
    setSaving(true);
    setError('');
    try {
      const payload: FichaFGSecciones = {
        mision: nodosToStringPlano(secciones.mision ?? []),
        funciones: nodosToFuncionesArray(secciones.funciones ?? []),
        competencias: nodosToStringPlano(secciones.competencias ?? []),
        relaciones: nodosToStringPlano(secciones.relaciones ?? []),
        indicadores: nodosToStringPlano(secciones.indicadores ?? []),
      };
      await actualizarFichaRolGobernanzaContenido(tipoRolId, payload);
      showToast(panel.fichaGuardada ?? 'Ficha guardada');
      onMarkClean();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
    } finally {
      setSaving(false);
    }
  }

  async function handleCancelar() {
    setError('');
    if (!fichaExists) {
      onMarkClean();
      return;
    }
    setLoading(true);
    try {
      const res = await fetchFichaRolGobernanzaContenido(tipoRolId);
      setSecciones(extractSeccionesFG(res.data.contenidoJson));
      onMarkClean();
    } catch {
      // silencioso
    } finally {
      setLoading(false);
    }
  }

  if (loading) return <LoadingSpinner size="sm" muted />;

  if (!fichaExists) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <p style={{ color: '#64748B', fontSize: 13, marginBottom: 16 }}>
          {panel.fichaNoExiste ?? 'Este rol no tiene ficha FG asociada.'}
        </p>
        <button
          onClick={handleCreate}
          disabled={creating}
          style={{
            padding: '10px 24px',
            background: creating ? '#94A3B8' : '#1F3864',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 600,
            cursor: creating ? 'wait' : 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {creating
            ? (panel.creandoFicha ?? 'Creando...')
            : (panel.crearFicha ?? 'Crear ficha de rol de gobernanza')}
        </button>
      </div>
    );
  }

  return (
    <div>
      {toast && (
        <div
          style={{
            marginBottom: 12,
            padding: '8px 14px',
            background: '#F0FDF4',
            border: '1px solid #BBF7D0',
            borderRadius: 6,
            fontSize: 12,
            color: '#16A34A',
          }}
        >
          {toast}
        </div>
      )}
      {error && (
        <div
          style={{
            marginBottom: 12,
            padding: '8px 14px',
            background: '#FEF2F2',
            border: '1px solid #FECACA',
            borderRadius: 6,
            fontSize: 12,
            color: '#DC2626',
          }}
        >
          {error}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {SECCIONES.map((s) => (
          <SeccionFicha
            key={s.id}
            titulo={(panel[s.labelKey] as string | undefined) ?? s.fallbackLabel}
            nodosIniciales={secciones[s.id] ?? []}
            focused={focusedSeccion === s.id}
            onFocus={() => setFocusedSeccion(s.id)}
            onUpdate={(nodos) => {
              setSecciones((prev) => ({ ...prev, [s.id]: nodos }));
              onMarkDirty();
            }}
          />
        ))}
      </div>

      <div style={footerSticky}>
        <button onClick={handleCancelar} disabled={!isDirty} style={btnSecondary(!isDirty)}>
          {panel.cancelar ?? 'Cancelar'}
        </button>
        <button
          onClick={handleGuardar}
          disabled={!isDirty || saving}
          style={btnPrimary(!isDirty || saving)}
        >
          {saving ? (panel.saving ?? 'Guardando...') : (panel.guardar ?? 'Guardar')}
        </button>
      </div>
    </div>
  );
}

interface SeccionFichaProps {
  titulo: string;
  nodosIniciales: ProseMirrorNode[];
  focused: boolean;
  onFocus: () => void;
  onUpdate: (nodos: ProseMirrorNode[]) => void;
}

function SeccionFicha({ titulo, nodosIniciales, focused, onFocus, onUpdate }: SeccionFichaProps) {
  const editor = useFichaEditor({ nodosIniciales, onUpdate, onFocus });
  return (
    <section>
      <h3
        style={{
          margin: '0 0 6px',
          fontSize: 12,
          fontWeight: 700,
          color: '#1F3864',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        {titulo}
      </h3>
      {focused && editor && <FichaToolbar editor={editor} />}
      <div
        style={{
          border: '1px solid #E5E7EB',
          borderTopLeftRadius: focused ? 0 : 6,
          borderTopRightRadius: focused ? 0 : 6,
          borderBottomLeftRadius: 6,
          borderBottomRightRadius: 6,
          padding: 12,
          minHeight: 90,
          background: '#fff',
          fontSize: 13,
          lineHeight: 1.5,
        }}
      >
        <EditorContent editor={editor} />
      </div>
    </section>
  );
}

const footerSticky: React.CSSProperties = {
  position: 'sticky',
  bottom: 0,
  marginTop: 20,
  padding: '12px 0',
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 10,
  background: '#fff',
  borderTop: '1px solid #E5E7EB',
};

const btnSecondary = (disabled: boolean): React.CSSProperties => ({
  padding: '8px 20px',
  background: 'transparent',
  border: '1px solid #D1D5DB',
  borderRadius: 6,
  fontSize: 13,
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontFamily: 'inherit',
  color: disabled ? '#9CA3AF' : '#374151',
  opacity: disabled ? 0.6 : 1,
});

const btnPrimary = (disabled: boolean): React.CSSProperties => ({
  padding: '8px 24px',
  background: disabled ? '#94A3B8' : '#16A34A',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 700,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.7 : 1,
  fontFamily: 'inherit',
});
